import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import AudioPlayer from '../components/AudioPlayer';
import { useUser } from '../context/UserProvider';
import { useApi } from '../context/ApiProvider';
import loadMp3File from '../utils/loadMp3File';
import AudioPlayerLoading from '../components/AudioPlayerLoading';
import { on, off } from '../utils/events';
import { timeFormat } from '../utils/timeFormat';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { RestartAlt, PlayArrow } from '@mui/icons-material';
import ShareLinkModal from '../components/ShareLinkModal';
import { useSnackbar } from 'notistack';
import { getShareToken, getShareContent } from '../utils/shareToken';

export const Play = createContext({
  isPlaying: false,
  setIsPlaying: () => {},
  playingMetaId: null,
  setPlayingMetaId: () => {},
  playingProgress: 0,
  setPlayingProgress: () => {},
  playingTrackIndexInSet: null,
  setPlayingTrackIndexInSet: () => {},
  playPause: () => {},
  audioRef: null,
  createAudioRef: () => {},
  playingSet: null,
  onSetEnded: () => {},
  trackVolume: 1,
  setTrackVolume: () => {},
  audioDuration: null,
  shouldShowPlaybackModal: false,
  setPlaybackTime: 0,
  isLoadingMetadata: false,
  shouldShowShareModal: false,
  showShareModal: () => {},
});

export const usePlay = () => {
  const context = useContext(Play);
  if (!context) {
    throw new Error('usePlay must be used within a PlayProvider');
  }
  return context;
};

function PlayProvider({ children }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [playingMetaId, setPlayingMetaId] = useState(null);
  const [playingProgress, setPlayingProgress] = useState(0);
  // Use ref to store current progress so context value object doesn't need to change
  const playingProgressRef = useRef(0);

  // Update ref whenever progress state changes
  useEffect(() => {
    playingProgressRef.current = playingProgress;
  }, [playingProgress]);

  const [audioRef, setAudioRef] = useState(null);
  const [playingSet, setPlayingSet] = useState(null);
  const [playingTrackIndexInSet, setPlayingTrackIndexInSet] = useState(null);
  const [queue, setQueue] = useState([]);
  const [trackVolume, setTrackVolume] = useState(1);
  const [loading, setLoading] = useState(false);
  // UI-only: true once the initial /queue response has come back, so the
  // player can tell "still loading" apart from "nothing queued"
  const [queueChecked, setQueueChecked] = useState(false);
  const [audioDuration, setAudioDuration] = useState(null);
  const [shouldShowPlaybackModal, setShouldShowPlaybackModal] = useState(false);
  const [setPlaybackTime, setSetPlaybackTime] = useState(false);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  const [shouldShowShareModal, setShouldShowShareModal] = useState(false);
  const [shareModalData, setShareModalData] = useState({
    contentType: null,
    contentName: null,
    contentId: null,
  });

  const { user, firebaseUser, initialized } = useUser();
  const api = useApi();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (user) loadQueue();
    if (!user) {
      setPlayingMetaId(null);
      setPlayingSet(null);
      setPlayingTrackIndexInSet(null);
      setQueue([]);
      setQueueChecked(false);
      audioRef?.pause();
    }
  }, [user]);

  useEffect(() => {
    function handleTokenRefreshed() {
      if (user && !playingSet) {
        loadQueue();
      }
    }
    on('tokenRefreshed', handleTokenRefreshed);
    return () => off('tokenRefreshed', handleTokenRefreshed);
  }, [user, playingSet]);

  async function loadQueue() {
    setLoading(true);
    // the player only needs the current item and what comes after it; the
    // queue popup loads earlier items itself
    const response = await api.get('/queue', { before: 0 });
    setLoading(false);
    setQueueChecked(true);
    if (response.ok) {
      const queueBody = response.body;
      setQueue(queueBody);
      const currentItem = queueBody.find((item) => item.is_current);
      const firstItem = currentItem || queueBody[0];
      if (!firstItem) return;
      setPlayingSet(firstItem.set_meta);
      setPlayingMetaId(firstItem.set_meta_id);
      return createAudioRef(firstItem.set_meta_id, false, true); // autoPlay=false (paused after reload), suppressResumeModal=true
    } else {
      console.log(response);
    }
  }

  async function loadSet(setToLoad, suppressResumeModal = false) {
    setLoading(true);
    const setId = setToLoad || playingMetaId;
    if (!setId) return;
    const response = await api.get(`/sets/${setId}`);
    setLoading(false);
    if (response.ok) {
      const set = response.body;
      setPlayingSet(set);
      setPlayingMetaId(setId);
      updateQueueOnSetPlay(setId);

      let showModal = false;
      // Only show modal if NOT suppressed AND set has saved timestamp AND switching sets
      // Don't show modal if user is already playing this set (they're just pausing/resuming)
      if (
        !suppressResumeModal &&
        set?.user_fav?.current_timestamp > 0 &&
        set?.id != playingMetaId
      ) {
        showModal = true;
        setShouldShowPlaybackModal(true);
        setSetPlaybackTime(set.user_fav.current_timestamp);
      }

      return { set, showModal };
    } else {
      console.log(response);
    }
  }

  async function createAudioRef(
    setToLoad,
    autoPlay = true,
    suppressResumeModal = false
  ) {
    if (audioRef) {
      audioRef.pause();
    }

    if (setToLoad) {
      const result = await loadSet(setToLoad, suppressResumeModal);
      if (!result?.set) return;

      const { set, showModal } = result;
      const mp3File = loadMp3File(set?.mp3_permalink);
      if (mp3File) {
        setIsLoadingMetadata(true);
        const audio = new Audio(mp3File);
        // Ensure metadata is fetched so duration becomes available without playback
        audio.preload = 'metadata';
        // Attach listeners to update duration in a stable state
        const onMeta = () => {
          const d =
            Number.isFinite(audio.duration) && audio.duration > 0
              ? audio.duration
              : null;
          setAudioDuration(d);
          setIsLoadingMetadata(false);
        };
        audio.addEventListener('loadedmetadata', onMeta);
        audio.addEventListener('durationchange', onMeta);
        // Kick off loading of metadata explicitly
        try {
          audio.load();
        } catch (_) {
          // Some browsers may no-op; it's safe to ignore
        }
        // Prime initial duration if already known
        const initial =
          Number.isFinite(audio.duration) && audio.duration > 0
            ? audio.duration
            : null;
        setAudioDuration(initial);
        if (initial) {
          setIsLoadingMetadata(false);
        }
        setAudioRef(audio);

        // Set initial timestamp if there's a saved position and no modal
        const startTime =
          set?.user_fav?.current_timestamp > 0 && !showModal
            ? set.user_fav.current_timestamp
            : 0;
        if (startTime > 0) {
          audio.currentTime = startTime;
          setPlayingProgress(startTime);
        } else {
          setPlayingProgress(0);
        }

        // Auto-play if requested and no modal is shown
        if (autoPlay && !showModal) {
          // Wait for metadata to load before playing
          const playWhenReady = () => {
            if (audio.readyState >= 1) {
              // Metadata is loaded
              playPause(audio, startTime);
            } else {
              // Wait for metadata
              audio.addEventListener(
                'loadedmetadata',
                () => {
                  playPause(audio, startTime);
                },
                { once: true }
              );
            }
          };
          playWhenReady();
        }

        return { audio, showModal, startTime };
      } else {
        console.log('No mp3 file found');
      }
    }
  }

  function playPause(ref, startTime) {
    const audio = ref || audioRef;
    if (audio) {
      if (startTime) {
        audio.currentTime = startTime;
      }
      if (!audio.paused && audio.currentTime > 0 && !audio.ended) {
        audio.pause();
        setIsPlaying(false);
      } else {
        audio.play();
        setIsPlaying(true);
      }
      audio.volume = trackVolume;
    }
  }

  async function updateQueueOnSetPlay(set_id) {
    const response = await api.put(`/queue/new-current`, {
      user_id: user.id,
      is_current: true,
      set_meta_id: set_id,
    });
    if (!response.ok) {
      console.log(response);
    }
  }

  async function onSetEnded(setId) {
    const isSetOnQueue = queue.find((item) => item.set_meta_id === setId);
    if (isSetOnQueue) {
      setLoading(true);
      const response = await api.put(`/queue/next`);
      setLoading(false);
      if (response.ok && !isLastCurrentItem()) {
        playQueueItem();
      }
    } else {
      playQueueItem();
    }
  }

  function isLastCurrentItem() {
    // by order, not position: the loaded queue starts at the current item,
    // and orders can have gaps
    const currentItem = queue.find((item) => item.is_current);
    if (!currentItem) return true;
    return !queue.some((item) => item.order > currentItem.order);
  }

  async function playQueueItem() {
    const ref = await loadQueue();
    if (ref) {
      playPause(ref);
    }
  }

  function resumePlaybackTime(resume) {
    setShouldShowPlaybackModal(false);
    const startAt = resume ? setPlaybackTime : 0;
    if (audioRef) {
      audioRef.currentTime = startAt;
      playPause(audioRef, startAt);
    }
  }

  function showShareModal(contentType, contentName, contentId) {
    setShareModalData({
      contentType,
      contentName,
      contentId,
    });
    setShouldShowShareModal(true);
  }

  function handleShareModalClose() {
    setShouldShowShareModal(false);
    // Mark modal as shown for this session
    const shareContent = getShareContent();
    if (shareContent) {
      const token = getShareToken();
      if (token) {
        sessionStorage.setItem(`kip_share_modal_shown_${token}`, 'true');
      }
    }
  }

  async function handleShareQueue() {
    if (shareModalData.contentType === 'set' && shareModalData.contentId) {
      const body = {
        user_id: user.id,
        set_meta_id: shareModalData.contentId,
      };
      const response = await api.post('/queue', body);
      if (response.ok) {
        enqueueSnackbar('Set added to queue!');
      } else {
        if (response.status === 404) {
          enqueueSnackbar('This set is already in the queue.', {
            variant: 'info',
          });
        } else {
          enqueueSnackbar('Something went wrong, please try again!', {
            variant: 'error',
          });
        }
      }
    }
    handleShareModalClose();
  }

  async function handleSharePlay() {
    if (shareModalData.contentType === 'set' && shareModalData.contentId) {
      await createAudioRef(shareModalData.contentId, true);
    } else if (
      shareModalData.contentType === 'track' &&
      shareModalData.contentId
    ) {
      // For tracks, use the same logic as PlayTrackButton
      const response = await api.get(
        `/set_tracks/play/${shareModalData.contentId}`
      );
      if (response.ok) {
        const { set_meta_id, start_time } = response.body;
        if (playingMetaId === set_meta_id) {
          audioRef.currentTime = start_time;
          playPause(audioRef);
        } else {
          const result = await createAudioRef(set_meta_id, false);
          if (result?.audio) {
            result.audio.currentTime = start_time;
            playPause(result.audio, start_time);
          }
        }
        enqueueSnackbar('Playing track!');
      }
    }
    handleShareModalClose();
  }

  async function handleShareBuy() {
    if (shareModalData.contentType === 'track' && shareModalData.contentId) {
      // Get track data to find purchase URL
      const trackResponse = await api.get(
        `/tracks/${shareModalData.contentId}`
      );
      if (trackResponse.ok) {
        const track = trackResponse.body;
        const purchaseUrl =
          track.purchase_url ||
          `https://www.beatport.com/search?q=${encodeURIComponent(
            track.artist
          )}%20-%20${encodeURIComponent(track.title)}`;

        // Open purchase URL
        window.open(purchaseUrl, '_blank');

        // Track purchase
        const purchaseResponse = await api.put(
          `/favs/track/${shareModalData.contentId}/purchase`
        );
        if (purchaseResponse.ok) {
          enqueueSnackbar('Track bought!');
        }
      }
    }
    handleShareModalClose();
  }

  // Memoize the context value but EXCLUDE playingProgress from dependencies
  // This prevents the context value object from changing when only progress updates
  // Note: playingProgress value will be stale in the context object, but components
  // that need current progress (like AudioPlayer) manage their own state anyway
  const contextValue = useMemo(
    () => ({
      isPlaying,
      setIsPlaying,
      playingMetaId,
      setPlayingMetaId,
      playingProgress: playingProgressRef.current, // Read from ref, always current
      setPlayingProgress,
      playingTrackIndexInSet,
      setPlayingTrackIndexInSet,
      playPause,
      audioRef,
      createAudioRef,
      playingSet,
      onSetEnded,
      trackVolume,
      setTrackVolume,
      audioDuration,
      shouldShowPlaybackModal,
      setShouldShowPlaybackModal,
      setPlaybackTime,
      isLoadingMetadata,
      shouldShowShareModal,
      showShareModal,
    }),
    [
      isPlaying,
      playingMetaId,
      // Intentionally exclude playingProgress to prevent re-renders
      playingTrackIndexInSet,
      audioRef,
      playingSet,
      trackVolume,
      audioDuration,
      shouldShowPlaybackModal,
      setPlaybackTime,
      isLoadingMetadata,
      shouldShowShareModal,
    ]
  );

  return (
    <Play.Provider value={contextValue}>
      <>
        {children}
        {(user || firebaseUser) &&
          (playingSet && !loading ? (
            <AudioPlayer />
          ) : (
            <AudioPlayerLoading
              empty={!playingSet && (queueChecked || (initialized && !user))}
            />
          ))}
        <Dialog
          open={shouldShowPlaybackModal}
          onClose={() => setShouldShowPlaybackModal(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">Continue Playback?</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Last time you listened to this set you left it at{' '}
              {timeFormat(setPlaybackTime)}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => resumePlaybackTime(false)}
              variant="outlined"
              startIcon={<RestartAlt />}
            >
              Restart
            </Button>
            <Button
              onClick={() => resumePlaybackTime(true)}
              variant="contained"
              autoFocus
              startIcon={<PlayArrow />}
            >
              Continue
            </Button>
          </DialogActions>
        </Dialog>
        <ShareLinkModal
          open={shouldShowShareModal}
          onClose={handleShareModalClose}
          contentType={shareModalData.contentType}
          contentName={shareModalData.contentName}
          onQueue={handleShareQueue}
          onPlay={handleSharePlay}
          onBuy={handleShareBuy}
        />
      </>
    </Play.Provider>
  );
}

PlayProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export default PlayProvider;
